import Constants from './constants';
import { applicant, coApplicant } from '../components/customer/config/form_fields';

class CustomerHelper {
  static forceLength(str) {
    return str.length <= 8 ? str : `${str.slice(0, 8)}...`;
  }

  static formatName(firstName, lastName) {
    return lastName ? `${this.forceLength(lastName)}, ${this.forceLength(firstName)}` : this.forceLength(firstName);
  }

  static fullName(firstName, lastName) {
    return lastName ? `${firstName} ${lastName}` : firstName;
  }

  static determineSignerRoles(customer) {
    const signerRoles = [];
    if (customer.cemail) signerRoles.push(Constants.SIGNER_ROLES.HOMEOWNER_PRIMARY_SIGNER);
    if (customer.csecondaryemail) signerRoles.push(Constants.SIGNER_ROLES.HOMEOWNER_SECONDARY_SIGNER);
    return signerRoles;
  }

  static getCustomerName(customer) {
    const { id, cfirstname, clastname } = customer;
    const name = cfirstname ? this.formatName(cfirstname, clastname) : `Customer ID: ${id}`;
    return name;
  }

  static getCustomerFullName(customer) {
    const { customerFirstName, customerLastName } = customer;
    const name = customerFirstName ? this.fullName(customerFirstName, customerLastName) : `${customerLastName}`;
    return name;
  }

  static validatePersonalInfo({ cfirstname, clastname, cphone1, cemail }) {
    if (!cfirstname) {
      throw new Error('First name is required');
    }
    if (!clastname) {
      throw new Error('Last name is required');
    }
    if (!cphone1) {
      throw new Error('Phone is required');
    }
    if (!cemail) {
      throw new Error('Email is required');
    }
  }

  static validateCoAppPersonalInfo({ csecondaryfirstname, csecondarylastname, csecondaryphone, csecondaryemail }) {
    if (!csecondaryfirstname) {
      throw new Error('Co-Applicant First name is required');
    }
    if (!csecondarylastname) {
      throw new Error('Co-Applicant Last name is required');
    }
    if (!csecondaryphone) {
      throw new Error('Co-Applicant Phone is required');
    }
    if (!csecondaryemail) {
      throw new Error('Co-Applicant Email is required');
    }
  }

  static validateAddressInfo({ caddress1, ccity, cstate, czip }) {
    if (!caddress1) {
      throw new Error('Address is required');
    }
    if (!ccity) {
      throw new Error('City is required');
    }
    if (!cstate) {
      throw new Error('State is required');
    }
    if (!czip) {
      throw new Error('ZIP Code is required');
    }
  }

  static validateMailingAddress({ caddress1Mail, ccityMail, cstateMail, czipMail }) {
    if (!caddress1Mail) {
      throw new Error('Mailing Address is required');
    }
    if (!ccityMail) {
      throw new Error('Mailing City is required');
    }
    if (!cstateMail) {
      throw new Error('Mailing State is required');
    }
    if (!czipMail) {
      throw new Error('Mailing ZIP Code is required');
    }
  }

  static hasPersonalInfo(customer) {
    return [applicant.firstName, applicant.lastName, applicant.phone, applicant.email].some((field) => customer[field]);
  }

  static hasAddressInfo(customer) {
    return [applicant.address, applicant.city, applicant.state, applicant.zip].some((field) => customer[field]);
  }

  static hasCoApp(customer) {
    return [coApplicant.firstName, coApplicant.lastName, coApplicant.email, coApplicant.phone].some(
      (field) => customer[field]
    );
  }

  static hasMailingInfo(customer) {
    return [applicant.mailingAddress, applicant.mailingCity, applicant.mailingState, applicant.mailingZip].some(
      (field) => customer[field]
    );
  }

  static mailingSameAsInstallationAddress = (formValues) =>
    !(
      (formValues[applicant.mailingAddress] !== '' &&
        formValues[applicant.mailingAddress] !== formValues[applicant.address]) ||
      (formValues[applicant.mailingCity] !== '' && formValues[applicant.mailingCity] !== formValues[applicant.city]) ||
      (formValues[applicant.mailingState] !== '' &&
        formValues[applicant.mailingState] !== formValues[applicant.state]) ||
      (formValues[applicant.mailingZip] !== '' && formValues[applicant.mailingZip] !== formValues[applicant.zip])
    );

  static validateCoAppIfExists(customer) {
    if (this.hasCoApp(customer)) {
      this.validateCoAppPersonalInfo(customer);
    }
  }

  static validateMailingIfExists(customer) {
    if (this.hasMailingInfo(customer)) {
      this.validateMailingAddress(customer);
    }
  }

  static validateEmail(emailInput) {
    const validLocalCharacters = "abcdefg.hijklmnopqrstuvwxyz!#$%&'*/=?^_+-`{|}~0123456789";

    // Email does not contain exactly 1 '@' symbol
    if (!(emailInput.match(/@/g) || []).length) {
      return false;
    }

    let [localSegment, domainSegment] = emailInput.split('@');

    if (localSegment.length === 0) {
      return false;
    }

    if (domainSegment.length < 3) {
      return false;
    }

    // Consecutive '.' at the beginning of local segment is illegal
    if ((localSegment.match(/^\.\..*/g) || []).length > 0) {
      return false;
    }

    // Local segment must contain only legal characters
    for (let i = 0; i < localSegment.length; ++i) {
      let char = localSegment[i];
      if (!validLocalCharacters.includes(char.toLowerCase())) {
        return false;
      }
    }

    // An email must contain a top-level domain (TLD)
    //
    // We consider a TLD to exist if there exists a '.', and the last '.' has
    // valid characters after it.
    let domainParts = domainSegment.split('.');
    if (domainParts.length === 1) {
      return false;
    }
    if (domainParts[domainParts.length - 1] === '') {
      return false;
    }

    if (emailInput && emailInput.includes('@.')) {
      return false;
    }

    return true;
  }

  static isCancelled(customer) {
    return customer.status.toLowerCase() === 'cancelled';
  }
  static isOnHold(customer) {
    return customer.holdCategorySku;
  }
}

export default CustomerHelper;
